import { matcher } from '@shared/search'
import { type Editor, type Range } from '@tiptap/core'
import {
  Code,
  Heading2,
  Heading3,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Quote,
  Radical,
  Sigma,
  Table,
  Type,
} from 'lucide-react'
import { type MessageKey } from '@/i18n/messages'
import { type SuggestItem } from './suggest'

// `/` in a card: insert one of the blocks a card can hold (shared/card-schema.ts),
// the same blocks the AI writes as markdown.

type Command = {
  key: string
  icon: SuggestItem['icon']
  label: MessageKey
  /** Other words it is found by, in both languages. */
  words: string
  run: (editor: Editor, range: Range) => void
}

const block =
  (fn: (c: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>) =>
  (editor: Editor, range: Range) => {
    fn(editor.chain().focus().deleteRange(range)).run()
  }

export function slashCommands(
  onMath: (editor: Editor, kind: 'inline' | 'block') => void,
): Command[] {
  return [
    {
      key: 'text',
      icon: Type,
      label: 'slash.text',
      words: 'text paragraph 文字 段落',
      run: block((c) => c.setParagraph()),
    },
    {
      key: 'h2',
      icon: Heading2,
      label: 'slash.h2',
      words: 'heading h2 title 標題 小標',
      run: block((c) => c.setNode('heading', { level: 2 })),
    },
    {
      key: 'h3',
      icon: Heading3,
      label: 'slash.h3',
      words: 'heading h3 標題 小標',
      run: block((c) => c.setNode('heading', { level: 3 })),
    },
    {
      key: 'bullet',
      icon: List,
      label: 'slash.bullet',
      words: 'list bullet ul 清單 列表 項目',
      run: block((c) => c.toggleBulletList()),
    },
    {
      key: 'ordered',
      icon: ListOrdered,
      label: 'slash.ordered',
      words: 'list ordered numbered ol 編號 清單 列表',
      run: block((c) => c.toggleOrderedList()),
    },
    {
      key: 'task',
      icon: ListChecks,
      label: 'slash.task',
      words: 'todo task checklist checkbox 待辦 勾選 清單',
      run: block((c) => c.toggleTaskList()),
    },
    {
      key: 'table',
      icon: Table,
      label: 'slash.table',
      words: 'table grid 表格',
      run: block((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true })),
    },
    {
      key: 'code',
      icon: Code,
      label: 'slash.code',
      words: 'code 程式碼 程式',
      run: block((c) => c.toggleCodeBlock()),
    },
    {
      key: 'quote',
      icon: Quote,
      label: 'slash.quote',
      words: 'quote blockquote 引用 引言',
      run: block((c) => c.toggleBlockquote()),
    },
    {
      key: 'divider',
      icon: Minus,
      label: 'slash.divider',
      words: 'divider rule hr line 分隔線 分隔',
      run: block((c) => c.setHorizontalRule()),
    },
    {
      key: 'math',
      icon: Sigma,
      label: 'slash.math',
      words: 'math formula equation latex 數學 公式 方程式',
      run: (editor, range) => {
        editor.chain().focus().deleteRange(range).run()
        onMath(editor, 'block')
      },
    },
    {
      key: 'inlineMath',
      icon: Radical,
      label: 'slash.inlineMath',
      words: 'math inline formula latex 行內 數學 公式',
      run: (editor, range) => {
        editor.chain().focus().deleteRange(range).run()
        onMath(editor, 'inline')
      },
    },
  ]
}

export function slashItems(
  commands: Command[],
  t: (key: MessageKey) => string,
  query: string,
): SuggestItem[] {
  const score = matcher(query)
  return commands
    .map((c) => ({ c, s: query.trim() ? score(`${t(c.label)} ${c.words}`) : 1 }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .map(({ c }) => ({ key: c.key, icon: c.icon, label: t(c.label), run: c.run }))
}

/** `/` opens at the start of a block, after a space, or after CJK text; not in a path or URL. */
export function slashAllowed(before: string): boolean {
  const c = before.at(-1)
  return c === undefined || /\s/.test(c) || (c.codePointAt(0) ?? 0) > 0x2e80
}
